import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MessageCircle, ChevronLeft, ChevronRight, Send, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useViewPreferences } from "@/hooks/use-view-preferences";
import {
  useTelegramConnection,
  useTelegramChats,
  useTelegramMessages,
  useSendTelegramMessage,
} from "@/hooks/useTelegram";
import { useCommunicationAnalyze } from "@/hooks/useCommunicationAnalyze";
import { useIntegrationIndexMap, getIntegrationIndexStatus } from "@/hooks/useIntegrationIndexMap";
import { CommunicationInboxLayout } from "@/components/communications/CommunicationInboxLayout";
import { CommunicationMessageList } from "@/components/communications/CommunicationMessageList";
import { CommunicationAgentResultsDialog } from "@/components/communications/CommunicationAgentResultsDialog";
import { AgentClickUpResultsDialog } from "@/components/communications/AgentClickUpResultsDialog";
import { AgentTelegramResultsDialog } from "@/components/communications/AgentTelegramResultsDialog";
import { IntegrationRagActionBar } from "@/components/integrations/IntegrationRagActionBar";
import { IntegrationIndexBadge } from "@/components/integrations/IntegrationIndexBadge";
import { TelegramConnectDialog } from "@/components/telegram/TelegramConnectDialog";
import { TelegramAgentPermissionsCard } from "@/components/telegram/TelegramAgentPermissionsCard";
import { filterCommunicationItems, sortCommunicationItems } from "@/lib/communications/filterSortMessages";
import { telegramMessageToInboxItem } from "@/lib/telegram/toInboxItem";
import { formatTelegramMarkdown, telegramMetadata } from "@/lib/integrationRag/formatTelegram";
import { telegramService } from "@/services/telegramService";
import {
  integrationRagService,
  saveIntegrationLibraryBackup,
} from "@/services/integrationRagService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import {
  ensureTelegramDocumentPermission,
  ensureTelegramRagPermission,
} from "@/lib/telegramAgentPermissions";
import type { CommunicationSortField } from "@/types/communications";
import type { TelegramMessage } from "@/types/telegram";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 30;

export default function TelegramInbox() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [connectOpen, setConnectOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [offsetId, setOffsetId] = useState<number | undefined>();
  const [offsetStack, setOffsetStack] = useState<(number | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Record<string, true>>({});
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [composeText, setComposeText] = useState("");
  const [indexing, setIndexing] = useState(false);
  const [indexProgress, setIndexProgress] = useState<string | null>(null);
  const [saveToLibrary, setSaveToLibrary] = useState(false);

  const { viewMode, setViewMode, sortField, setSortField, sortDirection, setSortDirection } =
    useViewPreferences<CommunicationSortField>({
      storageKey: "communications.telegram",
      defaultViewMode: "list",
      defaultSortField: "date",
      defaultSortDirection: "desc",
    });

  const connection = useTelegramConnection(user?.id);
  const analyze = useCommunicationAnalyze();
  const indexMap = useIntegrationIndexMap("telegram");
  const sendMutation = useSendTelegramMessage();

  const listParams = useMemo(() => ({ limit: PAGE_SIZE, offsetId }), [offsetId]);

  const {
    data: chatsData,
    isLoading: chatsLoading,
    isFetching: chatsFetching,
    refetch: refetchChats,
    isError: chatsError,
    error: chatsErr,
  } = useTelegramChats(user?.id, connection.connected, { limit: 50 });

  const {
    data: messagesData,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
    refetch: refetchMessages,
  } = useTelegramMessages(user?.id, activeChatId, connection.connected && !!activeChatId, listParams);

  const filteredMessages = useMemo(() => {
    const items = (messagesData?.messages ?? []).map(telegramMessageToInboxItem);
    const filtered = filterCommunicationItems(items, searchQuery);
    return sortCommunicationItems(filtered, sortField, sortDirection);
  }, [messagesData?.messages, searchQuery, sortField, sortDirection]);

  const activeChat = chatsData?.chats.find((c) => c.id === activeChatId);

  const resetPagination = useCallback(() => {
    setOffsetId(undefined);
    setOffsetStack([undefined]);
    setPageIndex(0);
  }, []);

  const handleRefresh = useCallback(() => {
    void refetchChats();
    if (activeChatId) void refetchMessages();
  }, [refetchChats, refetchMessages, activeChatId]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: true };
    });
  }, []);

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setSelectedIds({});
        return;
      }
      const next: Record<string, true> = {};
      for (const item of filteredMessages) next[item.id] = true;
      setSelectedIds(next);
    },
    [filteredMessages]
  );

  const fetchMessagesForIndex = useCallback(async (): Promise<TelegramMessage[]> => {
    const ids = Object.keys(selectedIds);
    const out: TelegramMessage[] = [];
    for (const id of ids) {
      const summary = messagesData?.messages.find((m) => m.id === id);
      if (summary?.body) {
        out.push(summary);
        continue;
      }
      if (activeChatId) {
        try {
          out.push(await telegramService.getMessage(activeChatId, id));
        } catch {
          if (summary) out.push(summary);
        }
      }
    }
    return out;
  }, [selectedIds, messagesData?.messages, activeChatId]);

  const handleIndexRaw = useCallback(async () => {
    const messages = await fetchMessagesForIndex();
    if (messages.length === 0) return;
    setIndexing(true);
    try {
      const items = messages.map((m) => ({
        source: "telegram" as const,
        externalId: m.id,
        title: m.subject,
        content: formatTelegramMarkdown(m),
        metadata: telegramMetadata(m),
        remoteUpdatedAt: m.date,
      }));
      await integrationRagService.indexItems(items, {
        contentMode: "raw",
        onProgress: (done, total) => setIndexProgress(`${done}/${total}`),
      });
      if (saveToLibrary) {
        await saveIntegrationLibraryBackup(items, { includeInRag: false });
      }
      void queryClient.invalidateQueries({ queryKey: ["integration-index", "telegram"] });
      toast({ title: "Indexed for RAG", description: `${items.length} message(s) embedded.` });
    } catch (e: unknown) {
      toast({
        title: "Index failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setIndexing(false);
      setIndexProgress(null);
    }
  }, [fetchMessagesForIndex, saveToLibrary, queryClient, toast]);

  const handleIndexAgentResult = useCallback(
    async (result: { itemId: string; content: string; itemLabel?: string }) => {
      try {
        await ensureTelegramRagPermission();
      } catch (e: unknown) {
        toast({
          title: "Permission required",
          description: e instanceof Error ? e.message : "Enable RAG permission",
          variant: "destructive",
        });
        return;
      }
      setIndexing(true);
      try {
        await integrationRagService.indexItems(
          [
            {
              source: "telegram",
              externalId: `telegram:agent:${result.itemId}`,
              title: result.itemLabel || "Telegram analysis",
              content: result.content,
              metadata: { agentResult: true },
            },
          ],
          { contentMode: "agent" }
        );
        toast({ title: "Agent output indexed" });
      } finally {
        setIndexing(false);
      }
    },
    [toast]
  );

  const handlePushSelectedToDocument = useCallback(async () => {
    try {
      await ensureTelegramDocumentPermission();
    } catch (e: unknown) {
      toast({
        title: "Permission required",
        description: e instanceof Error ? e.message : "Enable document permission",
        variant: "destructive",
      });
      return;
    }
    const messages = await fetchMessagesForIndex();
    const body = messages.map((m) => formatTelegramMarkdown(m)).join("\n\n---\n\n");
    try {
      await knowledgeBaseService.createKnowledgeBaseItem({
        title: `Telegram: ${activeChat?.title || "Messages"}`,
        content: body,
        type: "note",
        includeInRag: false,
      });
      toast({ title: "Document created", description: "Saved to your library as a note." });
    } catch (e: unknown) {
      toast({
        title: "Failed to create document",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [fetchMessagesForIndex, activeChat?.title, toast]);

  const handleAnalyze = useCallback(async () => {
    if (!selectedAgentId || !activeChatId) return;
    const ids = Object.keys(selectedIds);
    const items = [];
    for (const id of ids) {
      const summary = messagesData?.messages.find((m) => m.id === id);
      let body = summary?.body || summary?.preview || "";
      if (!body) {
        try {
          const detail = await telegramService.getMessage(activeChatId, id);
          body = detail.body || detail.preview;
        } catch {
          /* preview */
        }
      }
      items.push({
        id,
        label: summary?.subject || activeChat?.title || id,
        subject: summary?.subject || activeChat?.title || "",
        from: summary?.from || "",
        to: activeChatId,
        date: summary?.date,
        body,
        contentType: "telegram" as const,
      });
    }
    await analyze.runBatchAnalyze(selectedAgentId, items);
  }, [
    selectedAgentId,
    selectedIds,
    messagesData?.messages,
    activeChatId,
    activeChat?.title,
    analyze,
  ]);

  const handleSend = useCallback(async () => {
    if (!activeChatId || !composeText.trim()) return;
    try {
      await sendMutation.mutateAsync({ chatId: activeChatId, text: composeText.trim() });
      setComposeText("");
      toast({ title: "Sent" });
    } catch (e: unknown) {
      toast({
        title: "Send failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  }, [activeChatId, composeText, sendMutation, toast]);

  const handleSortChange = useCallback(
    (field: CommunicationSortField, direction: typeof sortDirection) => {
      setSortField(field);
      setSortDirection(direction);
    },
    [setSortField, setSortDirection]
  );

  const selectedDetail = messagesData?.messages.find((m) => m.id === activeMessageId);

  const integrationToolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <IntegrationRagActionBar
        selectedCount={Object.keys(selectedIds).length}
        indexing={indexing}
        indexProgress={indexProgress}
        saveToLibrary={saveToLibrary}
        onSaveToLibraryChange={setSaveToLibrary}
        onIndexRaw={() => void handleIndexRaw()}
        onAnalyze={() => void handleAnalyze()}
        showAnalyze={false}
        disabled={!connection.connected}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={Object.keys(selectedIds).length === 0}
        onClick={() => void handlePushSelectedToDocument()}
      >
        <FileText className="h-4 w-4 mr-1" />
        Create document
      </Button>
    </div>
  );

  return (
    <>
      <CommunicationInboxLayout
        icon={<MessageCircle className="h-6 w-6 text-primary" />}
        title="Telegram"
        description="View chats, send messages, analyze with AI agents"
        connected={connection.connected}
        connectionLabel={connection.connectionLabel}
        onConnect={() => setConnectOpen(true)}
        onDisconnect={() =>
          void connection.disconnect().catch((e) =>
            toast({ title: "Error", description: e.message, variant: "destructive" })
          )
        }
        isDisconnecting={connection.isDisconnecting}
        onRefresh={handleRefresh}
        isRefreshing={chatsFetching || messagesFetching}
        isLoading={chatsLoading || connection.isLoadingStatus}
        isError={chatsError}
        errorMessage={chatsErr instanceof Error ? chatsErr.message : undefined}
        searchQuery={searchQuery}
        onSearchChange={(q) => {
          setSearchQuery(q);
          resetPagination();
        }}
        searchPlaceholder="Search messages..."
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        selectedAgentId={selectedAgentId}
        onAgentChange={setSelectedAgentId}
        selectedCount={Object.keys(selectedIds).length}
        onAnalyzeSelected={() => void handleAnalyze()}
        isAnalyzing={analyze.isAnalyzing}
        batchProgress={
          analyze.batchProcessing
            ? {
                completed: analyze.batchProcessing.completed,
                total: analyze.batchProcessing.total,
                currentLabel: analyze.batchProcessing.currentLabel,
              }
            : undefined
        }
        integrationScope="telegram"
        integrationToolbar={integrationToolbar}
        disabled={analyze.isAnalyzing || indexing}
        cardTitle={activeChat?.title || "Select a chat"}
        cardDescription="Telegram messages from your connected account"
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-1 border rounded-lg overflow-hidden max-h-[520px] overflow-y-auto">
            {(chatsData?.chats ?? []).map((chat) => (
              <button
                key={chat.id}
                type="button"
                className={cn(
                  "w-full text-left px-3 py-2 border-b hover:bg-muted/50 transition-colors",
                  activeChatId === chat.id && "bg-muted"
                )}
                onClick={() => {
                  setActiveChatId(chat.id);
                  setActiveMessageId(null);
                  resetPagination();
                  setSelectedIds({});
                }}
              >
                <p className="font-medium text-sm truncate">{chat.title}</p>
                {chat.lastMessagePreview && (
                  <p className="text-xs text-muted-foreground truncate">{chat.lastMessagePreview}</p>
                )}
              </button>
            ))}
            {!chatsLoading && (chatsData?.chats.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground p-4">No chats found</p>
            )}
          </div>

          <div className="lg:col-span-4 space-y-3">
            {!activeChatId ? (
              <p className="text-sm text-muted-foreground text-center py-12">
                Select a chat to view messages
              </p>
            ) : (
              <>
                <CommunicationMessageList
                  items={filteredMessages}
                  viewMode={viewMode}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onSelectAllChange={handleSelectAllChange}
                  onSelectItem={setActiveMessageId}
                  activeId={activeMessageId}
                  disabled={analyze.isAnalyzing || indexing}
                  emptyMessage={searchQuery ? "No messages match your search" : "No messages"}
                  renderTrailingBadge={(item) => (
                    <IntegrationIndexBadge
                      status={getIntegrationIndexStatus(item.id, indexMap)}
                    />
                  )}
                />

                {messagesData?.nextOffsetId && (
                  <div className="flex justify-center gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pageIndex === 0}
                      onClick={() => {
                        const prev = pageIndex - 1;
                        setPageIndex(prev);
                        setOffsetId(offsetStack[prev]);
                      }}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const next = pageIndex + 1;
                        setOffsetStack((s) => [...s, messagesData.nextOffsetId]);
                        setOffsetId(messagesData.nextOffsetId);
                        setPageIndex(next);
                      }}
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t">
                  <Textarea
                    placeholder="Write a message..."
                    value={composeText}
                    onChange={(e) => setComposeText(e.target.value)}
                    rows={2}
                    className="resize-none"
                  />
                  <Button
                    onClick={() => void handleSend()}
                    disabled={sendMutation.isPending || !composeText.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {connection.connected && (
          <div className="mt-6">
            <TelegramAgentPermissionsCard
              permissions={connection.agentPermissions}
              onChange={(next) => void connection.updatePermissions(next)}
            />
          </div>
        )}
      </CommunicationInboxLayout>

      <TelegramConnectDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onConnected={() => void connection.refetchStatus()}
      />

      <CommunicationAgentResultsDialog
        open={analyze.resultsOpen}
        onOpenChange={analyze.setResultsOpen}
        results={analyze.results}
        onIndexResult={(r) =>
          void handleIndexAgentResult({
            itemId: r.itemId,
            content: r.content,
            itemLabel: r.itemLabel,
          })
        }
      />

      {analyze.clickUpActionContext && (
        <AgentClickUpResultsDialog
          open={analyze.clickUpActionOpen}
          onOpenChange={analyze.setClickUpActionOpen}
          agent={analyze.clickUpActionContext.agent}
          teamId={analyze.clickUpActionContext.teamId}
          meetings={analyze.clickUpActionContext.meetings}
          handoffSource="telegram"
          returnPath="/communication/telegram"
          items={analyze.lastItems}
          results={analyze.results}
          forceManualPost
        />
      )}

      {analyze.telegramActionContext && (
        <AgentTelegramResultsDialog
          open={analyze.telegramActionOpen}
          onOpenChange={analyze.setTelegramActionOpen}
          agent={analyze.telegramActionContext.agent}
          chatId={analyze.telegramActionContext.chatId}
          results={analyze.telegramActionContext.results}
          items={analyze.telegramActionContext.items}
        />
      )}

      {activeMessageId && selectedDetail && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-base">{selectedDetail.subject}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm whitespace-pre-wrap">
            {selectedDetail.body || selectedDetail.preview}
          </CardContent>
        </Card>
      )}
    </>
  );
}
